import React from 'react'
import { icons as lucideIcons } from 'lucide-react'

/**
 * Icon values are stored as a family-prefixed string: `"lucide:ThumbsUp"` or
 * `"fa:thumbs-up"`. New families (e.g. `mui:`) can be added here + in IconField
 * without touching consumers.
 */
export type IconFamily = 'lucide' | 'fa'

export type ParsedIcon = { family: IconFamily; name: string }

export function parseIcon(value?: string): ParsedIcon | null {
  if (!value) return null
  const idx = value.indexOf(':')
  if (idx <= 0) return null
  const family = value.slice(0, idx)
  const name = value.slice(idx + 1)
  if (!name) return null
  if (family === 'lucide' || family === 'fa') return { family, name }
  return null
}

const lucideRegistry = lucideIcons as Record<
  string,
  React.ComponentType<{ size?: number | string; color?: string; className?: string }>
>

export const LUCIDE_ICON_NAMES: string[] = Object.keys(lucideRegistry)

export type IconProps = {
  value?: string
  size?: number | string
  color?: string
  className?: string
}

/** Renders an icon from a family-prefixed value. Returns null if unresolved. */
export const Icon = ({ value, size = 24, color, className }: IconProps) => {
  const parsed = parseIcon(value)
  if (!parsed) return null

  if (parsed.family === 'fa') {
    return (
      <i
        className={`fa fa-${parsed.name}${className ? ` ${className}` : ''}`}
        style={{ fontSize: size, color, lineHeight: 1 }}
        aria-hidden="true"
      />
    )
  }

  const LucideIcon = lucideRegistry[parsed.name]
  if (!LucideIcon) return null
  return <LucideIcon size={size} color={color} className={className} />
}
